// The ringer's part on the page: the engraving (score.svg from the practice export,
// every notehead and rest tagged with its beat) and an overlay drawn into it, with a
// cell for each measure, the section, the measure being played and the cursor.
// The way the measures are found is the practice player's (music-games player.js).
import type { Piece } from './practice-core';

const NS = 'http://www.w3.org/2000/svg';
const fraction = (v: string | undefined) => { const s = String(v).split('/'); return Number(s[0]) / (s.length === 2 ? Number(s[1]) : 1); };

export interface Cell { measure: number; left: number; end: number; top: number; bottom: number; system: number; points: [number, number][]; rect: SVGRectElement }

export class ScoreView {
  svg: SVGSVGElement;
  cells: Cell[] = [];
  private piece: Piece;
  private section: SVGPathElement;
  private now: SVGRectElement;
  private cursor: SVGLineElement;
  private draft: SVGRectElement[] = [];
  private layer: SVGGElement;
  private root: SVGGElement;

  constructor(svg: SVGSVGElement, piece: Piece) {
    this.svg = svg; this.piece = piece;
    const pageGroup = document.createElementNS(NS, 'g');
    pageGroup.setAttribute('id', 'bl-page');
    while (svg.firstChild) pageGroup.appendChild(svg.firstChild);
    svg.appendChild(pageGroup);
    this.root = pageGroup;
    const M = piece.measures;
    const measureAt = (beat: number) => { let i = M.length - 1; while (i > 0 && M[i].start > beat + 1e-7) i--; return i + 1; };
    const groups = new Map<number, { x: number; y: number; w: number; h: number; beat: number }[]>();
    svg.querySelectorAll<SVGGElement>('.practice-note,.practice-rest').forEach(el => {
      const beat = fraction(el.dataset.beat), m = measureAt(beat), b = el.getBBox();
      if (!groups.has(m)) groups.set(m, []);
      groups.get(m)!.push({ x: b.x, y: b.y, w: b.width, h: b.height, beat });
    });
    const page = svg.viewBox.baseVal;
    this.layer = document.createElementNS(NS, 'g');
    this.layer.setAttribute('class', 'bells-overlay');
    const under = document.createElementNS(NS, 'g');
    this.root.insertBefore(under, this.root.firstChild);
    let system = -1, prevX = Infinity;
    const raw: { measure: number; x: number; right: number; ns: { x: number; y: number; w: number; h: number; beat: number }[]; system: number }[] = [];
    for (let m = 1; m <= piece.measureCount; m++) {
      const ns = groups.get(m) || [];
      if (!ns.length) continue;
      const x = Math.min(...ns.map(n => n.x)), right = Math.max(...ns.map(n => n.x + n.w));
      if (x < prevX - 1) system++;
      prevX = x;
      raw.push({ measure: m, x, right, ns, system });
    }
    for (let s = 0; s <= system; s++) {
      const row = raw.filter(r => r.system === s);
      const all = row.flatMap(r => r.ns);
      const top = Math.max(page.y, Math.min(...all.map(n => n.y)) - 3.2), bottom = Math.min(page.y + page.height, Math.max(...all.map(n => n.y + n.h)) + 2.2);
      row.forEach((r, i) => {
        const prev = row[i - 1], next = row[i + 1];
        const left = prev ? (prev.right + r.x) / 2 : r.x - 2.2;
        const end = next ? (r.right + next.x) / 2 : Math.min(page.x + page.width, r.right + 2.2);
        const pts = new Map<number, number>();
        for (const n of r.ns) { const cx = n.x + n.w / 2; pts.set(n.beat, Math.min(pts.get(n.beat) ?? Infinity, cx)); }
        const points = Array.from(pts.entries()).sort((a, b) => a[0] - b[0]) as [number, number][];
        const rect = document.createElementNS(NS, 'rect');
        rect.setAttribute('x', String(left)); rect.setAttribute('y', String(top));
        rect.setAttribute('width', String(end - left)); rect.setAttribute('height', String(bottom - top));
        rect.setAttribute('class', 'bells-cell'); rect.setAttribute('fill', 'transparent'); rect.dataset.measure = String(r.measure);
        this.layer.append(rect);
        this.cells.push({ measure: r.measure, left, end, top, bottom, system: s, points, rect });
      });
    }
    this.section = document.createElementNS(NS, 'path'); this.section.setAttribute('class', 'bells-section');
    this.section.setAttribute('fill', '#1E3A5F10'); this.section.setAttribute('stroke', '#1E3A5F'); this.section.setAttribute('stroke-width', '0.35'); under.append(this.section);
    this.now = document.createElementNS(NS, 'rect'); this.now.setAttribute('class', 'bells-now'); this.now.setAttribute('fill', '#C9A22740'); this.now.setAttribute('visibility', 'hidden'); under.append(this.now);
    this.cursor = document.createElementNS(NS, 'line'); this.cursor.setAttribute('class', 'bells-cursor'); this.cursor.setAttribute('stroke', '#722F37'); this.cursor.setAttribute('stroke-width', '0.45'); this.cursor.setAttribute('visibility', 'hidden');
    this.layer.append(this.cursor);
    this.root.append(this.layer);
  }

  // Each line of music as a band of the page, from halfway to the line above to halfway
  // to the line below, so the words, marks and bar numbers go with it.
  systems(): { y0: number; y1: number }[] {
    const page = this.svg.viewBox.baseVal, n = Math.max(0, ...this.cells.map(c => c.system)) + 1;
    const tb = Array.from({ length: n }, (_, s) => { const cs = this.cells.filter(c => c.system === s); return [cs[0].top, cs[0].bottom]; });
    return tb.map(([, ], s) => ({
      y0: s === 0 ? page.y : (tb[s - 1][1] + tb[s][0]) / 2,
      y1: s === n - 1 ? page.y + page.height : (tb[s][1] + tb[s + 1][0]) / 2,
    }));
  }
  width() { return this.svg.viewBox.baseVal.width; }
  left() { return this.svg.viewBox.baseVal.x; }

  // The measure at a point given in the page's own units.
  measureAt(x: number, y: number) {
    const c = this.cells.find(k => x >= k.left && x <= k.end && y >= k.top && y <= k.bottom);
    return c ? c.measure : null;
  }

  cell(measure: number) { return this.cells.find(c => c.measure === measure); }

  // The section, as one outline on each line of music it crosses.
  showSection(from: number | null, to: number | null) {
    if (from == null || to == null) { this.section.setAttribute('d', ''); return; }
    const rows = new Map<number, Cell[]>();
    for (const c of this.cells) if (c.measure >= from && c.measure <= to) { if (!rows.has(c.system)) rows.set(c.system, []); rows.get(c.system)!.push(c); }
    let d = '';
    rows.forEach(cs => {
      const l = cs[0].left, r = cs[cs.length - 1].end, t = cs[0].top, b = cs[0].bottom;
      d += `M${l},${t}H${r}V${b}H${l}Z`;
    });
    this.section.setAttribute('d', d);
  }

  showDraft(from: number | null, to: number | null) {
    this.cells.forEach(c => {
      const on = from != null && to != null && c.measure >= Math.min(from, to) && c.measure <= Math.max(from, to);
      c.rect.classList.toggle('draft', on); c.rect.setAttribute('fill', on ? '#1E3A5F22' : 'transparent');
    });
  }

  // Where the music is: the measure shaded and a line at the beat, placed between the
  // noteheads and rests either side of it.
  showPlace(measure: number | null, sourceBeat: number | null) {
    const c = measure != null ? this.cell(measure) : undefined;
    if (!c || sourceBeat == null) { this.now.setAttribute('visibility', 'hidden'); this.cursor.setAttribute('visibility', 'hidden'); return null; }
    this.now.setAttribute('visibility', 'visible');
    this.now.setAttribute('x', String(c.left)); this.now.setAttribute('y', String(c.top));
    this.now.setAttribute('width', String(c.end - c.left)); this.now.setAttribute('height', String(c.bottom - c.top));
    const m = this.piece.measures[measure! - 1];
    const pts: [number, number][] = [...c.points, [m.start + m.length, c.end - 1]];
    let x = pts[0][1];
    if (sourceBeat <= pts[0][0]) x = pts[0][1];
    else for (let i = 0; i < pts.length - 1; i++) {
      const [b0, x0] = pts[i], [b1, x1] = pts[i + 1];
      if (sourceBeat >= b0 && sourceBeat <= b1) { x = x0 + (x1 - x0) * ((sourceBeat - b0) / Math.max(1e-6, b1 - b0)); break; }
      x = x1;
    }
    this.cursor.setAttribute('visibility', 'visible');
    this.cursor.setAttribute('x1', String(x)); this.cursor.setAttribute('x2', String(x));
    this.cursor.setAttribute('y1', String(c.top + 1)); this.cursor.setAttribute('y2', String(c.bottom - 1));
    return c;
  }

  // The measure under the pointer, on the page itself or on one of the whole-piece
  // columns (which show the page again through <use>).
  measureFromPoint(clientX: number, clientY: number) {
    const el = document.elementFromPoint(clientX, clientY) as SVGElement | null;
    if (el?.dataset?.measure) return Number(el.dataset.measure);
    const svg = el?.closest?.('svg') as SVGSVGElement | null;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    return this.measureAt(p.x, p.y);
  }
}
